// filepath: apps/web/src/auth/AuthProvider.tsx
//
// FR-UI-CORE-2 AuthProvider wrapper.
//
// This component wraps react-oidc-context's <AuthProvider> with the
// resolved tenant config from the bootstrap step. It is the
// composition root for steps 3-4 of ARCHITECTURE_WEB.md §3
// (initialize `react-oidc-context` with the realm URL; OIDC flow).
//
// Responsibilities:
//   1. Translate the tenant-config bootstrap state into OIDC config.
//   2. Wire the OIDC user-manager with our memory-only userStore.
//   3. Surface the auth state to the rest of the SPA via the
//      `<AuthStateOutlet>` placeholder.
//   4. ISS-277: clean the OIDC callback payload (?code&state) from
//      the URL and land the user on "/" after a successful code
//      exchange (see handleSigninCallback below).
//   5. ISS-377 round 2 (B1 auto-bearer): register an OIDC user
//      accessor via `setOIDCUserAccessor(...)` so the default
//      `apiFetch` strategy reads the bearer AT REQUEST time. The
//      accessor returns the current `access_token` from the
//      `useAuth()` context; the accessor is re-registered on every
//      user-state change (mount, signin, silent refresh, signout)
//      so the bearer reflects the FRESH token.
//
// Non-responsibilities:
//   - The bootstrap shell (loading / error screens) is rendered by
//     the App composition root, not here.
//   - The /oidc-callback route is rendered by the App composition
//     root; this component only provides the OIDC context.

import React from "react";
import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import * as oidc from "oidc-client-ts";
import type { TenantConfig } from "./types";
import { buildOidcConfig } from "./oidcConfig";
import { handleSigninCallback } from "./signinCallback";
import { setOIDCUserAccessor } from "@/api";
import type { AuthContextProps } from "react-oidc-context";

// `handleSigninCallback` is extracted to `./signinCallback.ts` so
// the open-redirect guard (`isSafeSameOriginPath`) and the
// navigation side-effect are independently testable (see
// `signinCallback.test.ts`).

export interface AuthProviderProps {
  /**
   * The resolved tenant config from /api/tenant-config. Caller is
   * expected to have already validated the response (404 ⇒ 503 ⇒
   * network failures are surfaced via the parent's error screen).
   */
  tenantConfig: TenantConfig;
  /**
   * The children to render inside the OIDC context. Typically the
   * AuthGuard-wrapped app shell.
   */
  children: React.ReactNode;
}

/**
 * AuthProvider is a thin functional wrapper around
 * react-oidc-context's <AuthProvider>. It accepts the resolved
 * tenantConfig and feeds it to buildOidcConfig.
 *
 * Note: the underlying library is `react-oidc-context` which has
 * its own `AuthProvider` (renamed `OidcAuthProvider` on import to
 * avoid collision).
 */
export function AuthProvider({
  tenantConfig,
  children,
}: AuthProviderProps): React.ReactElement {
  // The library handles PKCE server-side; we don't pass our own
  // extraQueryParams or extraTokenParams beyond the defaults.
  // disablePKCE is explicitly false (the library default), and the
  // static-guard test asserts no client_secret is referenced.
  const settings = React.useMemo(
    () =>
      buildOidcConfig({
        tenantConfig,
        origin: typeof window !== "undefined" ? window.location.origin : "",
      }),
    [tenantConfig],
  );

  // INV-token-storage: do NOT pass a `userStore` option. The library
  // default is in-memory. The static-guard test asserts no
  // WebStorageStateStore is imported anywhere in src/auth/.
  //
  // We use a custom UserManager only when the library doesn't
  // already instance one internally. The default
  // `AuthProviderNoUserManagerProps` path is sufficient for us.
  void oidc; // mark module as referenced for the static-guard test

  return (
    <OidcAuthProvider {...settings} onSigninCallback={handleSigninCallback}>
      <OidcUserAccessorBridge>{children}</OidcUserAccessorBridge>
    </OidcAuthProvider>
  );
}

/**
 * Inner component that consumes the OIDC context (must be rendered
 * INSIDE `<OidcAuthProvider>`) and re-registers the user accessor
 * on every user-state change. The accessor returns the current
 * `AuthContextProps`; the apiFetch client's default OIDC strategy
 * reads the auth through this accessor at REQUEST time (NOT at
 * module-load time — both the access_token and the `signinSilent`
 * reference are mutable; AuthProvider re-registers after every
 * refresh).
 *
 * Implementation note: the accessor closure captures the latest
 * `auth` via the `useAuth()` hook — every time React re-renders this
 * component the closure is rebuilt with the fresh auth state, so
 * the accessor's caller (the apiFetch client) always sees the
 * latest state. The accessor itself is registered ONCE in a
 * useEffect with an empty dep array (mount/unmount only); the
 * closure delegates to a ref that is mutated on every render.
 */
function OidcUserAccessorBridge({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const auth = useAuth();
  // Mutable ref that the accessor closure delegates to. The
  // closure itself is registered once (in the useEffect below);
  // the ref is rebuilt on every render so the closure captures
  // the latest state on every call.
  const authRef = React.useRef<AuthContextProps>(auth);
  authRef.current = auth;
  React.useEffect(() => {
    // The accessor closure is stable across renders; it
    // delegates to the ref. AuthProvider re-registers the
    // accessor ONCE on mount; the ref-mutation strategy is the
    // standard "stable wrapper, mutable target" pattern.
    const accessor: () => AuthContextProps | null = () => authRef.current;
    setOIDCUserAccessor(accessor);
    return () => {
      // Unregister on unmount so a late apiFetch call (e.g. a
      // TanStack Query retry fired after the provider unmounts)
      // does NOT read a stale accessor.
      setOIDCUserAccessor(null);
    };
  }, []);
  return <>{children}</>;
}
